'use client'

import { useEffect, useRef, useState } from 'react'

/** True only after mount on the client (for opacity/blur entrance + bar fills that must START
 * from 0 and animate to target). Avoids a hydration flash by gating the post-mount state. */
export function useMounted() {
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  return mounted
}

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Reveal-on-scroll via IntersectionObserver. Adds `.is-in` to every `[data-reveal]` element
 * inside `rootRef` once it enters the viewport (idempotent — observer unobserves on reveal).
 * Under reduced-motion, reveals everything immediately so nothing stays blank.
 */
export function useReveal<T extends HTMLElement>() {
  const rootRef = useRef<T | null>(null)
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const targets = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]'))
    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      targets.forEach((t) => t.classList.add('is-in'))
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in')
            io.unobserve(e.target)
          }
        })
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
    )
    targets.forEach((t) => io.observe(t))
    return () => io.disconnect()
  }, [])
  return rootRef
}
